import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { Star, MessageCircle, ArrowDown } from "lucide-react";
import { PHONES } from "@/data";
import { scrollTo } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "A arte de", c: "text-[#f5f2ea]" },
  { t: "realçar a sua", c: "text-[#f5f2ea]" },
  { t: "beleza.", c: "gold-text italic" },
];

const Line = ({ text, cls, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${cls}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.14, ease: EASE }}
    >
      {text}
    </motion.span>
  </span>
);

const LogoCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 15 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset} className="relative">
      <motion.div
        data-testid="hero-logo-card"
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, scale: 0.9, rotateZ: -6 }}
        animate={{ opacity: 1, scale: 1, rotateZ: -3 }}
        transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-[6px] shadow-[0_40px_120px_-20px_rgba(212,175,55,0.35)] ring-1 ring-[#d4af37]/40"
      >
        <img src="/img/unhas-castanhas.webp" alt="Unhas castanhas com acabamento brilhante" className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent mix-blend-overlay" />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="topo" ref={ref} className="relative min-h-[100svh] overflow-hidden pt-28 pb-16 sm:pt-32">
      <div className="pointer-events-none absolute -top-40 right-[-10%] hidden md:block h-[620px] w-[620px] rounded-full bg-[#d4af37]/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: yText }} className="lg:col-span-7 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 flex flex-wrap items-center gap-4 text-xs tracking-[0.28em] uppercase text-zinc-300"
          >
            <span>Rejane Simões · Cabeleireiro &amp; Estética</span>
            <span data-testid="hero-rating" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 tracking-normal normal-case">
              <Star className="h-3.5 w-3.5 fill-[#d4af37] text-[#d4af37]" /> 5,0 · 12 críticas Google
            </span>
          </motion.div>
          <h1 data-testid="hero-title" className="font-serif text-[15vw] leading-[0.92] tracking-tight sm:text-7xl lg:text-[7.4rem]">
            {LINES.map((l, i) => <Line key={l.t} text={l.t} cls={l.c} i={i} />)}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-8 max-w-md text-base leading-relaxed text-zinc-300 sm:text-lg"
          >
            Cabelo, unhas e estética num espaço calmo e acolhedor em Póvoa de Santa Iria. Fale connosco e marque o seu momento.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <a
              data-testid="hero-whatsapp-btn"
              href={PHONES[0].wa}
              target="_blank"
              rel="noopener noreferrer"
              className="group gold-bg inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-5px_rgba(212,175,55,0.6)]"
            >
              <MessageCircle className="h-4 w-4" /> Marcar pelo WhatsApp
            </a>
            <button
              data-testid="hero-contacts-btn"
              onClick={() => scrollTo("contacto")}
              className="inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 text-sm text-white transition-colors hover:border-[#d4af37] hover:text-[#f3e5ab]"
            >
              Ver contactos <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.5, delay: 0.3, ease: EASE }}
            className="ml-auto w-[78%] overflow-hidden rounded-t-full"
          >
            <img src="/img/cabelo.webp" alt="Balayage loiro com ondas" className="aspect-[3/4] w-full object-cover" />
          </motion.div>
          <div className="absolute -bottom-6 left-0 w-[52%] sm:w-[46%]">
            <LogoCard />
          </div>
        </div>
      </div>
    </section>
  );
};
